/**
 * The CRM API client — leads, their trail, and the merge. Spec §H.
 *
 * **A lead is not a client.** It is somebody the firm has not yet made a record
 * for, and FR-H4's proposal acceptance is what converts one (Wave 5E). So
 * nothing here reads as a client: there is no `client_id` on a lead, no PAN,
 * and no route that creates an entity.
 *
 * **The PAN is a fingerprint on the service and never travels.** A lead carries
 * `has_pan`, a boolean, and that is deliberate — the value exists only as
 * `sha256(org_id + ':' + PAN)` in the database, and serving the digest would
 * put a stable per-person identifier in a browser's network log for the sake
 * of a column nothing renders. See `services/prodesk-service/app/models/crm.py`.
 */

import { api } from '../api'

// -- shapes ------------------------------------------------------------------

/** FR-H2's six, fixed. A firm does not add one — unlike a case's stages, which
 *  it configures per service (ADR-0009). */
export type LeadStage = 'new' | 'contacted' | 'qualified' | 'proposal_sent' | 'won' | 'lost'

/** FR-H1's three origins. */
export type LeadSource = 'site_form' | 'self_registration' | 'staff'

export type ActivityKind =
  | 'enquiry'
  | 'note'
  | 'call'
  | 'meeting'
  | 'stage_changed'
  | 'owner_changed'
  | 'acknowledgement'
  | 'merged'

export interface LeadRecord {
  id: string
  full_name: string
  email: string | null
  mobile: string | null
  organisation_name: string | null
  /** Whether the lead carries a PAN fingerprint. **Never the PAN.** */
  has_pan: boolean
  source: LeadSource
  stage: LeadStage
  lost_reason: string | null
  status: 'active' | 'merged'
  merged_into_id: string | null
  owner_user_id: string | null
  branch_id: string | null
  next_follow_up_on: string | null
  service_id: string | null
  /** What they asked about when it did **not** resolve to a service in the
   *  catalog. Kept rather than dropped: a firm that has not listed "trust
   *  registration" still wants the enquiry about it. */
  service_label: string | null
  /** FR-H1's "the page it came from", for a lead the website's form made. */
  page_path: string | null
  message: string | null
  notes: string | null
  person_id: string | null
  converted_client_id: string | null
  created_at: string
}

export interface LeadActivity {
  id: string
  lead_id: string
  kind: ActivityKind
  summary: string
  detail: Record<string, unknown>
  occurred_at: string
  actor_user_id: string | null
  /** `visitor` for anything an anonymous member of the public did. */
  actor_kind: string
}

export interface LeadDetail extends LeadRecord {
  activities: LeadActivity[]
  /** The leads that were merged **into** this one. Nothing is deleted, so they
   *  are still readable and their trail is folded into `activities`. */
  merged_leads: LeadRecord[]
}

export interface LeadListMeta {
  total: number
  limit: number
  offset: number
  counts: Partial<Record<LeadStage, number>>
  /** True when the caller lacks `can_view_all` and is seeing their own leads
   *  and the unclaimed ones (spec §P — an associate works "the leads they
   *  own"). The screen says so rather than silently showing a shorter list. */
  narrowed: boolean
}

// -- the client --------------------------------------------------------------

export const crm = {
  list: (
    params: {
      search?: string
      stage?: LeadStage | ''
      source?: LeadSource | ''
      owner_user_id?: string
      unowned?: boolean
      limit?: number
      offset?: number
    } = {},
  ) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '' && value !== false) query.set(key, String(value))
    }
    const suffix = query.toString() ? `?${query}` : ''
    return api<LeadRecord[]>(`/api/v1/leads${suffix}`)
  },
  get: (id: string) => api<LeadDetail>(`/api/v1/leads/${id}`),
  duplicates: (id: string) => api<LeadRecord[]>(`/api/v1/leads/${id}/duplicates`),
  vocabulary: () =>
    api<{
      stages: LeadStage[]
      sources: LeadSource[]
      activity_kinds: ActivityKind[]
      loggable_kinds: ActivityKind[]
    }>('/api/v1/leads/vocabulary'),

  create: (body: {
    full_name: string
    email?: string
    mobile?: string
    organisation_name?: string
    pan?: string
    message?: string
    service_slug?: string
    owner_user_id?: string
  }) => api<LeadRecord & { created: boolean }>('/api/v1/leads', { method: 'POST', body }),

  update: (
    id: string,
    body: Partial<{
      full_name: string
      email: string
      mobile: string
      organisation_name: string
      notes: string
      next_follow_up_on: string | null
      service_id: string | null
    }>,
  ) => api<LeadRecord>(`/api/v1/leads/${id}`, { method: 'PATCH', body }),

  /** FR-H2. `lost` needs a reason and the service refuses without one. */
  setStage: (id: string, stage: LeadStage, lostReason?: string) =>
    api<LeadRecord>(`/api/v1/leads/${id}/stage`, {
      method: 'POST',
      body: { stage, lost_reason: lostReason ?? null },
    }),

  /**
   * Take an unclaimed lead. The **service** reads the owner from the session:
   * this call sends no user id at all, so "take this one" cannot be edited
   * into "give this one to somebody else".
   */
  claim: (id: string) => api<LeadRecord>(`/api/v1/leads/${id}/claim`, { method: 'POST' }),

  /** Assign a colleague, or `null` to return the lead to the unclaimed queue
   *  — a real action for somebody who cannot take it. */
  setOwner: (id: string, ownerUserId: string | null) =>
    api<LeadRecord>(`/api/v1/leads/${id}/owner`, {
      method: 'POST',
      body: { owner_user_id: ownerUserId },
    }),

  logActivity: (id: string, body: { kind: 'note' | 'call' | 'meeting'; summary: string }) =>
    api<LeadActivity>(`/api/v1/leads/${id}/activities`, { method: 'POST', body }),

  /** FR-H6. `id` is retired into `into`; nothing is deleted. Needs
   *  `lead:can_delete` — the partner and the practice manager (spec §P). */
  merge: (id: string, into: string) =>
    api<{ lead: LeadRecord; merged: LeadRecord }>(`/api/v1/leads/${id}/merge`, {
      method: 'POST',
      body: { into },
    }),
}

// -- pure helpers, so the screen's rules are testable -------------------------

/** FR-H2's six, in pipeline order. Exported so a stage arriving from the wire
 *  can be checked rather than cast. */
export const LEAD_STAGES: LeadStage[] = [
  'new',
  'contacted',
  'qualified',
  'proposal_sent',
  'won',
  'lost',
]

export const STAGE_LABELS: Record<LeadStage, string> = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  proposal_sent: 'Proposal sent',
  won: 'Won',
  lost: 'Lost',
}

/** The two a lead does not come back from on its own. A fresh enquiry from a
 *  contact whose only lead is terminal opens a **new** lead — otherwise a live
 *  enquiry is buried inside a closed record. */
export const TERMINAL_STAGES: LeadStage[] = ['won', 'lost']

export const SOURCE_LABELS: Record<LeadSource, string> = {
  site_form: 'Your website',
  self_registration: 'Registered on your website',
  staff: 'Added by your firm',
}

/**
 * `meta` off the list response, read tolerantly.
 *
 * `api<T>` types `data` and leaves `meta` as `Record<string, unknown>` — one
 * type parameter, deliberately, because meta differs per route. So this is the
 * one place the list's meta is given a shape, and it defaults rather than
 * throws: a screen that renders no stage counts is a smaller defect than one
 * that renders nothing.
 */
export function listMeta(meta: Record<string, unknown> | undefined): LeadListMeta {
  const raw = meta ?? {}
  const counts = raw.counts
  return {
    total: typeof raw.total === 'number' ? raw.total : 0,
    limit: typeof raw.limit === 'number' ? raw.limit : 50,
    offset: typeof raw.offset === 'number' ? raw.offset : 0,
    counts:
      counts && typeof counts === 'object' && !Array.isArray(counts)
        ? (counts as Partial<Record<LeadStage, number>>)
        : {},
    narrowed: raw.narrowed === true,
  }
}

/**
 * One line under a lead: who they are and what they asked about.
 *
 * Falls back through the three things a lead can be identified by, in the order
 * a person would use them, and never renders "null".
 */
export function leadSubtitle(lead: LeadRecord, serviceName?: string | null): string {
  const who = [lead.organisation_name, lead.email ?? lead.mobile].filter(Boolean)
  const what = serviceName ?? lead.service_label
  return [...who, what ? `about ${what}` : null].filter(Boolean).join(' · ')
}

/**
 * Whether this lead may be moved.
 *
 * A merged lead has no position in the pipeline — the survivor does — and the
 * service answers 409. Hiding the control is the UI half of the same rule
 * (FiveOX ADR-0029: both, always).
 */
export function canMove(lead: LeadRecord): boolean {
  return lead.status === 'active'
}

/**
 * The copy for a lead the website's form made, with where it came from.
 *
 * `page_path` is the page the visitor was on, which is the difference between
 * "somebody enquired" and "somebody read your GST page and enquired" — the
 * second is what tells a firm which page is earning its keep.
 */
export function arrivalNote(lead: LeadRecord): string | null {
  if (lead.source === 'staff') return null
  const where = lead.page_path ? ` from ${lead.page_path}` : ''
  return lead.source === 'site_form'
    ? `Enquired through your website${where}.`
    : `Registered on your website${where}.`
}
